// Typed client for the settingsapi autofill endpoints.
// Endpoints are field-keyed: the drawer creates an isDraft field, runs
// generate/evaluate/test against it, then publishes on save. Which field collection
// (deal questions or contract workflow items) comes from the AutofillTarget context.
import { useMemo } from "react";
import useAPI, { useAPIMutation } from "@/services/api/hook";
import { CreateQuestionnaireQuestionSettings } from "@/pages/deals/types";
import { type AutofillTarget, useAutofillTarget } from "./autofillTarget";

export type AutofillConfidence = "high" | "medium" | "low";

export interface AutofillAssessment {
  confidence: AutofillConfidence;
  whatsMissing: string[];
}

export interface AutofillGeneratedFrom {
  label: string;
  displayType: string;
  helpText: string | null;
  options: string[];
}

export interface GenerateAutofillInstructionsResponse {
  instructions: string;
  assessment: AutofillAssessment;
  generatedFrom: AutofillGeneratedFrom;
  triggeringActivityTypes: string[];
  dependentFieldKeys: string[];
  error: string | null;
}

export interface EvaluateAutofillInstructionsResponse {
  assessment: AutofillAssessment;
  error: string | null;
}

export type AutofillTestRunStatus =
  | "queued"
  | "running"
  | "completed"
  | "cancelled"
  | "error";

const TERMINAL_TEST_RUN_STATUSES: AutofillTestRunStatus[] = [
  "completed",
  "cancelled",
  "error"
];

export const isTerminalTestRunStatus = (
  status: AutofillTestRunStatus | undefined
): boolean => !!status && TERMINAL_TEST_RUN_STATUSES.includes(status);

export interface AutofillCandidate {
  hasValue: boolean;
  rawValue: string;
  renderedValue: string;
  confidence: AutofillConfidence | null;
  citation: string;
  sourceActivityId: string;
}

// Async dry-run (CRM-7968): POST returns a queued run, then poll GET until terminal.
export interface AutofillTestRun {
  id: string;
  questionId: string;
  entityId: string;
  status: AutofillTestRunStatus;
  candidate: AutofillCandidate | null;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
  actions: string[];
}

export interface DreamerFlowGeneratedFrom {
  label: string;
  displayType: string;
  options: string[];
}

export interface DreamerFlow {
  version: number;
  locked: boolean;
  instructions: string;
  generatedFrom: DreamerFlowGeneratedFrom;
  assessment: AutofillAssessment;
  triggeringActivityTypes: string[];
  dependentFieldKeys: string[];
}

export interface CreateAutofillDraftResponse {
  id: string;
}

export type PublishAutofillQuestionData = Partial<CreateQuestionnaireQuestionSettings> &
  ({ isDraft: false; filledByDreamer: true; dreamerFlow: DreamerFlow }
    | { isDraft: false; filledByDreamer: false; dreamerFlow?: null });

export interface PublishAutofillQuestionResponse {
  id: string;
  isDraft: boolean | null;
  filledByDreamer: boolean | null;
  dreamerFlow: DreamerFlow | null;
}

// `questionId` is the field id of whichever target is configured: a deal question or a
// contract workflow item. It is renamed to the target's own path param on the way out.
interface FieldPathParams {
  questionId: string;
}
type TestRunPathParams = FieldPathParams & { testRunId: string };

const toRoutePathParams = (
  target: AutofillTarget,
  { questionId, ...rest }: FieldPathParams & Partial<TestRunPathParams>
): Record<string, string> => ({
  ...(rest as Record<string, string>),
  [target.fieldIdParam]: questionId
});

const useAutofillFieldMutation = <
  TVariables extends { pathParams: FieldPathParams },
  TResponse
>(
  route: keyof AutofillTarget["routes"],
  method: "POST" | "PUT" | "DELETE"
) => {
  const target = useAutofillTarget();
  const mutation = useAPIMutation<TVariables, TResponse>(target.routes[route], {
    method
  });
  const { mutate, mutateAsync } = mutation;
  return useMemo(() => {
    const remap = (variables: TVariables): TVariables => ({
      ...variables,
      pathParams: toRoutePathParams(target, variables.pathParams)
    });
    return {
      ...mutation,
      mutate: (variables: TVariables, options?: Parameters<typeof mutate>[1]) =>
        mutate(remap(variables), options),
      mutateAsync: (
        variables: TVariables,
        options?: Parameters<typeof mutateAsync>[1]
      ) => mutateAsync(remap(variables), options)
    };
  }, [mutation, mutate, mutateAsync, target]);
};

// Body comes from the target's `buildDraft`.
export const useCreateAutofillDraftQuestion = () => {
  const target = useAutofillTarget();
  return useAPIMutation<
    { data: Record<string, unknown> },
    CreateAutofillDraftResponse
  >(target.routes.createField, { method: "POST" });
};

export const useDeleteAutofillDraftQuestion = () =>
  useAutofillFieldMutation<{ pathParams: FieldPathParams }, void>(
    "field",
    "DELETE"
  );

export const useGenerateAutofillInstructions = () =>
  useAutofillFieldMutation<
    { pathParams: FieldPathParams; data: Record<string, never> },
    GenerateAutofillInstructionsResponse
  >("instructions", "POST");

export const useEvaluateAutofillInstructions = () =>
  useAutofillFieldMutation<
    { pathParams: FieldPathParams; data: { instructions: string } },
    EvaluateAutofillInstructionsResponse
  >("instructions", "PUT");

// Start a run: 202 with a queued AutofillTestRun. A new run supersedes any
// in-flight run for the same question (the backend cancels the old one).
export const useRunAutofillTest = () =>
  useAutofillFieldMutation<
    {
      pathParams: FieldPathParams;
      data: {
        entityId: string;
        instructions: string;
        triggeringActivityTypes: string[];
      };
    },
    AutofillTestRun
  >("testRuns", "POST");

// Poll a run by id every 2s until it reaches a terminal status.
export const useAutofillTestRunStatus = (
  questionId: string,
  testRunId: string | null
) => {
  const target = useAutofillTarget();
  return useAPI(
    target.routes.testRunById,
    {
      method: "GET",
      pathParams: toRoutePathParams(target, {
        questionId,
        testRunId: testRunId ?? ""
      })
    },
    {
      enabled: !!questionId && !!testRunId,
      refetchInterval: (query) =>
        isTerminalTestRunStatus(
          (query.state.data as AutofillTestRun | undefined)?.status
        )
          ? false
          : 2000
    }
  );
};

// Cancel a run by id; returns the (now cancelled or already-terminal) run.
export const useCancelAutofillTest = () =>
  useAutofillFieldMutation<{ pathParams: TestRunPathParams }, AutofillTestRun>(
    "testRunById",
    "DELETE"
  );

export const usePublishAutofillQuestion = () =>
  useAPIMutation<
    { pathParams: { questionId: string }; data: PublishAutofillQuestionData },
    PublishAutofillQuestionResponse
  >("settings/updateQuestionnaireQuestion", { method: "PUT" });
